import * as React from 'react';

import {NothingFoundEmptyState} from 'shells/empty-states/nothing-found-empty-state';
import {ErrorState} from 'shells/error-state/error-state';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {Table} from 'shells/table/table';
import {
    convertSortByToUrlSortParam,
    convertUrlSortParamToTableSortBy,
} from 'shells/table/table-sort-helpers';
import type {SortBy} from 'shells/table/types';

import type {NewsletterStubFragment as Newsletter} from 'nutshell-graphql-types';

import {EmptyState} from './empty-state';

import styles from './newsletters-list-view.module.css';

type Props = {
    isLoading: boolean;
    error: string | null | undefined;
    newsletters: Newsletter[];
    sortString: string;
    isFiltering: boolean;
    isShowingArchived: boolean;
    setIsAddingNewNewsletter: (arg1: boolean) => void;
    onChangeSort: (newSortString?: string | null | undefined) => void;
};

export function NewslettersListView(props: Props) {
    const columns = React.useMemo(
        () => [
            {
                id: 'name',
                Header: 'Name',
                accessor: (newsletter: Newsletter) => newsletter,
                dataType: 'linkedName',
            },
            {
                id: 'lastSentEdition',
                Header: 'Last sent edition',
                accessor: (newsletter: Newsletter) =>
                    newsletter.lastSentEdition ? newsletter.lastSentEdition.name : '-',
                dataType: 'string',
                shouldTruncate: true,
                minWidth: 150,
                width: 200,
                maxWidth: 200,
            },
            {
                id: 'lastSentDate',
                Header: 'Date last sent',
                accessor: (newsletter: Newsletter) =>
                    newsletter.lastSentEdition ? newsletter.lastSentEdition.sendTime : undefined,
                dataType: 'timestamp',
                minWidth: 150,
                width: 200,
                maxWidth: 200,
            },
            {
                id: 'editions',
                Header: '# Editions',
                accessor: (newsletter: Newsletter) => newsletter.editions.totalCount,
                dataType: 'string',
                minWidth: 120,
                width: 120,
                maxWidth: 120,
            },
            {
                id: 'recipients',
                Header: 'Recipients',
                accessor: (newsletter: Newsletter) =>
                    newsletter.engagementStats.totalSentCount === 0
                        ? '—'
                        : newsletter.engagementStats.totalSentCount.toLocaleString(),
                dataType: 'string',
                minWidth: 90,
                width: 90,
                maxWidth: 110,
            },
            {
                id: 'openRate',
                Header: 'Open rate',
                accessor: (newsletter: Newsletter) => newsletter.engagementStats.openRate,
                dataType: 'percentage',
                minWidth: 90,
                width: 90,
                maxWidth: 110,
            },
            {
                id: 'clickRate',
                Header: 'Click rate',
                accessor: (newsletter: Newsletter) => newsletter.engagementStats.clickRate,
                dataType: 'percentage',
                minWidth: 90,
                width: 90,
                maxWidth: 110,
            },
            {
                id: 'unsubscribeRate',
                Header: 'Unsubscribe rate',
                accessor: (newsletter: Newsletter) => newsletter.engagementStats.unsubscribeRate,
                dataType: 'percentage',
                minWidth: 130,
                width: 130,
                maxWidth: 150,
            },
            {
                id: 'bounceRate',
                Header: 'Bounce rate',
                accessor: (newsletter: Newsletter) =>
                    newsletter.engagementStats.permanentBounceRate,
                hasRightBorder: true,
                dataType: 'percentage',
                minWidth: 100,
                width: 100,
                maxWidth: 110,
            },
            {
                id: 'modifiedTime',
                Header: 'Last updated',
                accessor: (newsletter: Newsletter) => newsletter.modifiedTime,
                dataType: 'timestamp',
                minWidth: 150,
                width: 200,
                maxWidth: 200,
            },
            {
                id: 'createdTime',
                Header: 'Date created',
                accessor: (newsletter: Newsletter) => newsletter.createdTime,
                dataType: 'timestamp',
                minWidth: 150,
                width: 200,
                maxWidth: 200,
                hasRightBorder: true,
            },
        ],
        []
    );

    const handleSortByChange = (newSortBy: SortBy[]) => {
        const newSortString = convertSortByToUrlSortParam(newSortBy);

        props.onChangeSort(newSortString);
    };

    return (
        <div className={styles['newsletter-stats']}>
            <Table
                isResizable={true}
                hasHeaderBorder={true}
                hasHeaderCellBorders={true}
                hasHoverState={false}
                isCompact={true}
                stickyHeaders={true}
                hasBorders={true}
                isCheckboxSelectable={false}
                clickingSelectsRow={false}
                disableFilters={true}
                disableSorting={false}
                // Removes "triple sort" and forces there
                // always to be a asc/desc sort applied
                disableSortRemove={true}
                sortBy={convertUrlSortParamToTableSortBy(props.sortString)}
                onChangeSortBy={handleSortByChange}
                shouldPreventSelectAll={false}
                // @ts-expect-error
                columns={columns}
                data={props.newsletters}
                specialStates={[
                    {
                        shouldRender: () => Boolean(props.error),
                        component: <ErrorState errorMessage={props.error} />,
                    },
                    {
                        shouldRender: () => props.isLoading,
                        component: <LoadingPage padding={0} />,
                    },
                    {
                        shouldRender: () =>
                            !props.newsletters.length &&
                            !props.isLoading &&
                            (props.isFiltering || props.isShowingArchived),
                        component: (
                            <NothingFoundEmptyState
                                title='No newsletters were found'
                                subtitle='Try loosening your search!'
                            />
                        ),
                    },
                    {
                        shouldRender: () => !props.newsletters.length && !props.isLoading,
                        component: (
                            <EmptyState
                                onPrimaryCtaClick={() => props.setIsAddingNewNewsletter(true)}
                            />
                        ),
                    },
                ]}
            />
        </div>
    );
}
